import { PlanningsLegRunOperationEntity } from 'src/entities/PlanningsDelivery.entity';
import { LaneBerthReservationForPlan, ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

type Op = Pick<PlanningsLegRunOperationEntity, 'action' | 'arrivalAt' | 'departureAt' | 'shipmentOrderLegRunId'>;
type LegRun = Pick<
  ShipmentOrderLegRunForPlanEntity,
  'id' | 'loading_berth_reservations' | 'unloading_berth_reservations'
>;

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

function hasWindow(
  r: LaneBerthReservationForPlan,
): r is LaneBerthReservationForPlan & { reserved_from: string; reserved_to: string } {
  return Boolean(r.reserved_from && r.reserved_to);
}

/**
 * バース予約の時間外か。V3Timeline と同じ判定
 * (utils/berthReservationTime.util.ts:27-55) を、予約枠を legRun から引く形にしたもの。
 *
 * Why not 共通ユーティリティをそのまま使う: あちらは operation が
 * loading_berth_reservations を持つ前提だが、/map の operation
 * (PlanningsLegRunOperationEntity) は持たず、枠は legRun 側にある。
 *
 * 判定は地点（= 1 カード）の滞在区間で行う。有効な枠が 1 件も無ければ「予約なし」として
 * false（枠が無いことは違反ではない）。枠があってどれとも重ならないときだけ true。
 */
export function isOutsideBerthReservation(ops: readonly Op[], legRuns: readonly LegRun[]): boolean {
  if (ops.length === 0) return false;

  const arrivals = ops.map((op) => toMs(op.arrivalAt)).filter((ms): ms is number => ms != null);
  const departures = ops.map((op) => toMs(op.departureAt)).filter((ms): ms is number => ms != null);
  if (arrivals.length === 0 || departures.length === 0) return false;
  const [startMs, endMs] = [Math.min(...arrivals), Math.max(...departures)];

  const legRunById = new Map<number, LegRun>();
  legRuns.forEach((lr) => { if (lr.id != null) legRunById.set(lr.id, lr); });

  const reservations = ops.flatMap((op) => {
    const lr = legRunById.get(op.shipmentOrderLegRunId);
    if (!lr) return [];
    const list = op.action === '積' ? lr.loading_berth_reservations : lr.unloading_berth_reservations;
    return (list ?? []).filter(hasWindow);
  });
  if (reservations.length === 0) return false;

  return !reservations.some((r) => {
    const [fromMs, toMsValue] = [toMs(r.reserved_from), toMs(r.reserved_to)];
    if (fromMs == null || toMsValue == null) return false;
    return startMs <= toMsValue && endMs >= fromMs;
  });
}
